import { useQueryClient } from '@tanstack/react-query';
import { useCallback, useMemo } from 'react';
import type { SubmitHandler } from 'react-hook-form';

import { RequestQueryKeysE } from 'declarations/enums/react-query';
import type { NoopT } from 'declarations/types/common';
import type { SelectOptionT } from 'declarations/types/select';
import { getErrorMessage, getValidationErrorMessage } from 'helpers/apiError';
import { useFetchTicketPriorities } from 'hooks/api/useTicketPriorities';
import { useChangePriority } from 'hooks/api/useTickets';

import type { ChangeTicketPriorityFormValuesT } from '../ChangeTicketPriorityPopup';

type UseChangeTicketPriorityPopupT = {
  isChangingPriority: boolean;
  isPriorityChanged: boolean;
  isPriorityChangeError: boolean;
  ticketPrioritiesOptions: SelectOptionT[];
  isLoadingPriorityesOptions: boolean;
  changeTicketPriorityErrorMessage: string | undefined;
  changeTicketPriorityValidationErrorMessage: string | undefined;
  showPopupContent: boolean;
  handleChangeTicketPriority: SubmitHandler<ChangeTicketPriorityFormValuesT>;
  handleClosePopup: (onClosePopupCb: NoopT) => NoopT;
};

const useChangeTicketPriorityPopup = (): UseChangeTicketPriorityPopupT => {
  /**
   * Hooks
   */
  const queryClient = useQueryClient();

  /**
   * Api hooks
   */
  const ticketPrioritiesResult = useFetchTicketPriorities(true);

  const changePriorityMutation = useChangePriority({
    onSuccess: () => {
      queryClient.invalidateQueries([RequestQueryKeysE.FetchTicketMessages]);
      queryClient.invalidateQueries([RequestQueryKeysE.FetchTicket]);
      queryClient.invalidateQueries([RequestQueryKeysE.FetchTickets]);
    },
  });

  /**
   * Variables
   */
  const ticketPrioritiesOptions = useMemo(
    () =>
      ticketPrioritiesResult.data
        ? ticketPrioritiesResult.data?.map(({ id, name }) => ({
            label: name,
            value: id,
          }))
        : [],
    [ticketPrioritiesResult.data]
  );

  const changeTicketPriorityErrorMessage = useMemo(
    () => getErrorMessage(changePriorityMutation.error),
    [changePriorityMutation.error]
  );

  const changeTicketPriorityValidationErrorMessage = useMemo(
    () => getValidationErrorMessage(changePriorityMutation.error),
    [changePriorityMutation.error]
  );

  /**
   * Handlers
   */
  const resetChangeTicketPriorityMutationState = useCallback(() => {
    if (changePriorityMutation.isSuccess || changePriorityMutation.isError) {
      changePriorityMutation.reset();
    }
  }, [changePriorityMutation]);

  const handleClosePopup = useCallback(
    (onClosePopupCb: NoopT) => () => {
      onClosePopupCb();
      resetChangeTicketPriorityMutationState();
    },
    [resetChangeTicketPriorityMutationState]
  );

  const handleChangeTicketPriority = useCallback(
    (data: ChangeTicketPriorityFormValuesT) => {
      changePriorityMutation.mutate(data);
    },
    [changePriorityMutation]
  );

  return {
    isChangingPriority: changePriorityMutation.isLoading,
    isPriorityChanged: changePriorityMutation.isSuccess,
    isPriorityChangeError: changePriorityMutation.isError,
    ticketPrioritiesOptions,
    isLoadingPriorityesOptions: ticketPrioritiesResult.isLoading,
    changeTicketPriorityErrorMessage,
    changeTicketPriorityValidationErrorMessage,
    showPopupContent: changePriorityMutation.isIdle || changePriorityMutation.isLoading,
    handleChangeTicketPriority,
    handleClosePopup,
  };
};

export default useChangeTicketPriorityPopup;
